/**
 * @typedef {Object} AtRiskRelationship
 * @property {string} id - The SLS relationship id
 * @property {string} counterpartName - Display name of the person on the other end
 * @property {string} relationshipLabel - The relationship type, e.g. "Parent"
 */

/**
 * @typedef {Object} AtRiskRelationshipGroup
 * @property {string} personId - The moving person whose relationships are at risk
 * @property {string} personName - Display name of that moving person
 * @property {Array<AtRiskRelationship>} relationships - Only the relationships that actually break
 */

/**
 * Works out which relationships a move would actually break.
 *
 * A relationship only breaks when the counterpart is left behind. People moving together keep the
 * relationships between them, so those are never listed. An empty result means nothing is at risk
 * and the confirmation must be suppressed entirely — it fires once per action, not once per person.
 *
 * @param {Object} params
 * @param {Array<{id: string, name: string}>} params.movingPersons - Everyone selected to move
 * @param {Object.<string, Array<{id: string, otherPersonId: string, label: string, value: string}>>} params.relationshipsByPersonId
 *   SLS relationships keyed by the moving person's id
 * @param {Array<string>} [params.pendingMemberIds] - Ids already staged to join the same group; they
 *   move in the same save, so they count as moving together
 * @returns {Array<AtRiskRelationshipGroup>} One group per moving person that loses at least one relationship
 */
export const getAtRiskRelationshipGroups = ({
  movingPersons = [],
  relationshipsByPersonId = {},
  pendingMemberIds = [],
}) => {
  const movingIdSet = new Set([...movingPersons.map((person) => person.id), ...pendingMemberIds])

  return movingPersons.reduce((groups, movingPerson) => {
    const relationships = (relationshipsByPersonId[movingPerson.id] || [])
      // A counterpart who is also moving stays related, so their relationship is not at risk.
      .filter((relationship) => relationship.otherPersonId && !movingIdSet.has(relationship.otherPersonId))
      .map((relationship) => ({
        id: relationship.id,
        counterpartName: relationship.value,
        relationshipLabel: relationship.label,
      }))

    if (relationships.length > 0) {
      groups.push({ personId: movingPerson.id, personName: movingPerson.name, relationships })
    }
    return groups
  }, [])
}

/**
 * Whether the Previous Relationships confirmation needs to be shown for this move.
 *
 * @param {Array<AtRiskRelationshipGroup>} atRiskGroups - Result of {@link getAtRiskRelationshipGroups}
 * @returns {boolean}
 */
export const hasAtRiskRelationships = (atRiskGroups) => (atRiskGroups?.length || 0) > 0

export default getAtRiskRelationshipGroups
